<template>
  <div class="extras-mg-kanban">
    <div v-if="cargando" class="extras-mg-kanban__estado">Cargando…</div>

    <div v-else-if="!view.single_select_field" class="extras-mg-kanban__estado">
      Elige arriba el campo de seleccion unica que define las columnas.
    </div>

    <template v-else>
      <div v-if="hayMasFilas" class="extras-mg-kanban__tope">
        Se muestran las primeras {{ TOPE_TOTAL }} filas: la vista tiene mas.
      </div>

      <div class="extras-mg-kanban__pilas">
        <div
          v-for="pila in pilas"
          :key="pila.id === null ? 'sin-valor' : pila.id"
          class="extras-mg-kanban__pila"
          @dragover.prevent
          @drop="soltarEn(pila)"
        >
          <div class="extras-mg-kanban__pila-cabecera">
            <span
              class="extras-mg-kanban__etiqueta"
              :class="pila.color ? 'background-color--' + pila.color : ''"
            >
              {{ pila.nombre }}
            </span>
            <span class="extras-mg-kanban__cuenta">{{ pila.filas.length }}</span>
          </div>

          <div class="extras-mg-kanban__tarjetas">
            <RowCard
              v-for="fila in pila.filas"
              :key="fila.id"
              class="extras-mg-kanban__tarjeta"
              :fields="camposDeTarjeta"
              :row="fila"
              :workspace-id="database.workspace.id"
              :cover-image-field="campoDePortada"
              :decorations-by-place="decorationsByPlace"
              :draggable="puedeMover"
              @dragstart="empezarArrastre(fila)"
              @mousedown="alPresionar(fila)"
            ></RowCard>

            <div v-if="!pila.filas.length" class="extras-mg-kanban__vacia">
              Sin tarjetas
            </div>
          </div>
        </div>
      </div>
    </template>

    <RowEditModal
      ref="rowEditModal"
      enable-navigation
      :database="database"
      :table="table"
      :view="view"
      :all-fields-in-table="fields"
      :primary-is-sortable="true"
      :visible-fields="camposVisiblesOrdenados"
      :hidden-fields="camposOcultos"
      :rows="filas"
      :read-only="noSePuedeEditar"
      :show-hidden-fields="showHiddenFieldsInRowModal"
      @hidden="$emit('selected-row', undefined)"
      @toggle-hidden-fields-visibility="
        showHiddenFieldsInRowModal = !showHiddenFieldsInRowModal
      "
      @update="actualizarValor"
      @field-updated="$emit('refresh', $event)"
      @field-deleted="$emit('refresh')"
      @field-created="$emit('refresh')"
      @refresh-row="refrescarFila"
    ></RowEditModal>
  </div>
</template>

<script>
import { notifyIf } from '@baserow/modules/core/utils/error'
import RowCard from '@baserow/modules/database/components/card/RowCard'
import RowService from '@baserow/modules/database/services/row'

import vistaMG from '@extras_mg/mixins/vistaMG'

export default {
  name: 'KanbanView',
  components: { RowCard },
  mixins: [vistaMG],
  props: {
    database: { type: Object, required: true },
    table: { type: Object, required: true },
    view: { type: Object, required: true },
    fields: { type: Array, required: true },
    readOnly: { type: Boolean, required: true },
    storePrefix: { type: String, required: false, default: '' },
  },
  emits: ['refresh', 'selected-row'],
  data() {
    return {
      rutaDeFilas: 'kanban-mg',
      arrastrada: null,
      // Distingue un clic de un arrastre: sin esto, soltar una tarjeta en otra
      // columna abria ademas su ficha.
      arrastro: false,
      // La tarjeta que se apreto y todavia no se suelta.
      pendiente: null,
    }
  },
  computed: {
    campoDeColumnas() {
      return this.fields.find((f) => f.id === this.view.single_select_field)
    },
    /**
     * Mover una tarjeta de pila es escribir la columna del Kanban: si esa
     * columna está restringida para ti (permisos por columna), no se arrastra
     * en vez de dejarla ir y regresarla con un error.
     */
    puedeMover() {
      const campo = this.campoDeColumnas
      return (
        !this.readOnly &&
        !!campo &&
        this.$registry.get('field', campo.type).canWriteFieldValues(campo)
      )
    },
    campoDePortada() {
      const id = this.view.card_cover_image_field
      return this.fields.find((f) => f.id === id) || null
    },
    /**
     * Los campos de la tarjeta, sin el de columnas: repetirlo en cada tarjeta
     * de una pila solo dice lo que ya dice la cabecera de la pila.
     *
     * Solo para la tarjeta. La ficha de la fila recibe `camposVisiblesOrdenados`:
     * quitarle el campo de columnas lo dejaba fuera de las dos listas del modal
     * —ni visible ni oculto— y el estado no se podia ver ni cambiar desde ahi.
     */
    camposDeTarjeta() {
      return this.camposVisiblesOrdenados.filter(
        (f) => f.id !== this.view.single_select_field
      )
    },
    pilas() {
      const opciones = this.campoDeColumnas?.select_options || []
      const porOpcion = {}
      for (const o of opciones) {
        porOpcion[o.id] = { id: o.id, nombre: o.value, color: o.color, filas: [] }
      }
      const sinValor = { id: null, nombre: 'Sin valor', color: null, filas: [] }

      const llave = `field_${this.view.single_select_field}`
      for (const fila of this.filas) {
        const valor = fila[llave]
        if (valor && porOpcion[valor.id]) {
          porOpcion[valor.id].filas.push(fila)
        } else {
          sinValor.filas.push(fila)
        }
      }
      // "Sin valor" al final: es donde caen las que faltan por clasificar.
      return [...opciones.map((o) => porOpcion[o.id]), sinValor]
    },
  },
  watch: {
    'view.single_select_field'() {
      this.traer()
    },
  },
  mounted() {
    // UN solo listener para toda la vida del componente. El mixin tiene su
    // propio mounted; Vue corre los dos.
    window.addEventListener('mouseup', this.alSoltarElRaton)
  },
  beforeUnmount() {
    window.removeEventListener('mouseup', this.alSoltarElRaton)
  },
  methods: {
    empezarArrastre(fila) {
      this.arrastrada = fila
      this.arrastro = true
    },
    /**
     * El clic abre la ficha, pero solo si no se arrastro la tarjeta.
     *
     * Aqui solo se apunta cual tarjeta se apreto; quien decide es el mouseup,
     * y ese listener se registra UNA vez (ver mounted). Antes se registraba uno
     * nuevo por cada mousedown y se quitaba a si mismo al soltar, pero tras un
     * arrastre HTML5 el navegador no dispara mouseup: el listener se quedaba
     * colgado y el siguiente clic abria ademas la ficha de la tarjeta anterior.
     */
    alPresionar(fila) {
      this.arrastro = false
      this.pendiente = fila
    },
    alSoltarElRaton() {
      const fila = this.pendiente
      this.pendiente = null
      if (fila && !this.arrastro) {
        this.abrirFila(fila)
      }
    },
    async soltarEn(pila) {
      const fila = this.arrastrada
      this.arrastrada = null
      if (!fila || !this.puedeMover) {
        return
      }
      const llave = `field_${this.view.single_select_field}`
      const actual = fila[llave]?.id ?? null
      if (actual === pila.id) {
        return
      }
      const anterior = fila[llave]
      fila[llave] = pila.id
        ? { id: pila.id, value: pila.nombre, color: pila.color }
        : null
      try {
        const { data } = await RowService(this.$client).update(
          this.table.id,
          fila.id,
          { [llave]: pila.id },
          this.view.id
        )
        Object.assign(fila, data)
      } catch (e) {
        fila[llave] = anterior
        notifyIf(e, 'row')
      }
    },
  },
}
</script>

<style lang="scss">
/* Sin un solo color fijo, a proposito: build-theme.py invierte la luminosidad
   de todo hex que encuentre en el CSS compilado, asi que un color escrito aqui
   saldria invertido en uno de los dos temas. Todo hereda con currentColor y
   opacidades. */
.extras-mg-kanban {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.extras-mg-kanban__estado,
.extras-mg-kanban__tope {
  padding: 16px;
  font-size: 13px;
  opacity: 0.7;
}

.extras-mg-kanban__tope {
  padding: 6px 16px;
  font-size: 12px;
}

.extras-mg-kanban__pilas {
  display: flex;
  gap: 12px;
  padding: 12px;
  overflow-x: auto;
  align-items: flex-start;
  flex: 1;
}

.extras-mg-kanban__pila {
  flex: 0 0 280px;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  border-radius: 6px;
  background: color-mix(in srgb, currentColor 4%, transparent);
  padding: 8px;
}

.extras-mg-kanban__pila-cabecera {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.extras-mg-kanban__etiqueta {
  font-size: 12px;
  font-weight: 600;
  border-radius: 10px;
  padding: 2px 10px;
}

.extras-mg-kanban__cuenta {
  font-size: 11px;
  opacity: 0.6;
}

.extras-mg-kanban__tarjetas {
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 40px;
}

.extras-mg-kanban__tarjeta {
  cursor: pointer;
}

.extras-mg-kanban__vacia {
  font-size: 11px;
  opacity: 0.45;
  padding: 8px 2px;
}
</style>
